import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

// Companies API client (multi-company). Mirrors backend/src/routes/companies.ts:
// GET is available to any authenticated user (the sidebar switcher), writes
// require settings.canManageCompanies.

export interface Company {
  id: string;
  legalName: string;
  displayName: string | null;
  cui: string | null;
  regCom: string | null;
  address: string | null;
  city: string | null;
  county: string | null;
  country: string;
  iban: string | null;
  bank: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  logoUrl: string | null;
  defaultCurrency: string;
  vatPayer: boolean;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CompanyInput {
  legalName: string;
  displayName?: string | null;
  cui?: string | null;
  regCom?: string | null;
  address?: string | null;
  city?: string | null;
  county?: string | null;
  country?: string | null;
  iban?: string | null;
  bank?: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  logoUrl?: string | null;
  defaultCurrency?: 'RON' | 'EUR' | 'USD';
  vatPayer?: boolean;
}

// Administrators / sole associates with mandate history (CLE-ADMIN).
export type CompanyAdministratorRole = 'ADMINISTRATOR' | 'ASOCIAT_UNIC';

export interface CompanyAdministrator {
  id: string;
  companyId: string;
  personId: string;
  role: CompanyAdministratorRole;
  /** ISO date. */
  startDate: string;
  /** ISO date; null = active mandate. */
  endDate: string | null;
  createdAt: string;
  updatedAt: string;
  person: { id: string; fullName: string; avatarUrl: string | null };
}

export interface CompanyAdministratorInput {
  personId: string;
  role: CompanyAdministratorRole;
  /** ISO yyyy-mm-dd. */
  startDate: string;
  /** ISO yyyy-mm-dd; null = active mandate. */
  endDate?: string | null;
}

export interface DeleteCompanyResult {
  deleted: true;
  id: string;
  detachedClients: number;
  detachedSuppliers: number;
  detachedProjects: number;
  detachedInvoices: number;
  detachedFinancials: number;
  detachedBankStatements: number;
  deletedSmartbillInvoices: number;
}

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** All firms, default first. */
export async function listCompanies(): Promise<Company[]> {
  const res = await apiFetch<Envelope<{ companies: Company[] }>>('/api/companies', {
    headers: authHeaders(),
  });
  return res.data.companies;
}

export async function createCompany(input: CompanyInput): Promise<Company> {
  const res = await apiFetch<Envelope<{ company: Company }>>('/api/companies', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data.company;
}

export async function updateCompany(id: string, input: Partial<CompanyInput>): Promise<Company> {
  const res = await apiFetch<Envelope<{ company: Company }>>(`/api/companies/${id}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data.company;
}

export async function setDefaultCompany(id: string): Promise<Company> {
  const res = await apiFetch<Envelope<{ company: Company }>>(`/api/companies/${id}/default`, {
    method: 'POST',
    headers: authHeaders(),
  });
  return res.data.company;
}

export async function deleteCompany(id: string): Promise<DeleteCompanyResult> {
  const res = await apiFetch<Envelope<DeleteCompanyResult>>(`/api/companies/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return res.data;
}

// --- Administrators / sole associates (CLE-ADMIN) ---------------------------

export async function listCompanyAdministrators(
  companyId: string,
): Promise<CompanyAdministrator[]> {
  const res = await apiFetch<Envelope<{ administrators: CompanyAdministrator[] }>>(
    `/api/companies/${companyId}/administrators`,
    { headers: authHeaders() },
  );
  return res.data.administrators;
}

export async function createCompanyAdministrator(
  companyId: string,
  input: CompanyAdministratorInput,
): Promise<CompanyAdministrator> {
  const res = await apiFetch<Envelope<{ administrator: CompanyAdministrator }>>(
    `/api/companies/${companyId}/administrators`,
    { method: 'POST', headers: authHeaders(), body: JSON.stringify(input) },
  );
  return res.data.administrator;
}

export async function updateCompanyAdministrator(
  companyId: string,
  mandateId: string,
  input: Partial<CompanyAdministratorInput>,
): Promise<CompanyAdministrator> {
  const res = await apiFetch<Envelope<{ administrator: CompanyAdministrator }>>(
    `/api/companies/${companyId}/administrators/${mandateId}`,
    { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(input) },
  );
  return res.data.administrator;
}

export async function deleteCompanyAdministrator(
  companyId: string,
  mandateId: string,
): Promise<void> {
  await apiFetch<Envelope<{ deleted: true; id: string }>>(
    `/api/companies/${companyId}/administrators/${mandateId}`,
    { method: 'DELETE', headers: authHeaders() },
  );
}

// --- Company documents (certificat de înregistrare, acte constatatoare…) -----

export const COMPANY_DOCUMENT_CATEGORIES = [
  'REGISTRATION_CERTIFICATE',
  'CONSTATATOR',
  'STATUT',
  'OTHER',
] as const;
export type CompanyDocumentCategory = (typeof COMPANY_DOCUMENT_CATEGORIES)[number];

export interface CompanyDocument {
  id: string;
  companyId: string;
  name: string;
  description: string | null;
  category: CompanyDocumentCategory | string;
  fileType: string | null;
  fileSize: number | null;
  uploadedAt: string;
  downloadUrl: string | null;
}

export async function listCompanyDocuments(companyId: string): Promise<CompanyDocument[]> {
  const res = await apiFetch<Envelope<{ documents: CompanyDocument[] }>>(
    `/api/companies/${companyId}/documents`,
    { headers: authHeaders() },
  );
  return res.data.documents;
}

export async function requestCompanyDocumentUploadUrl(
  companyId: string,
  input: {
    fileName: string;
    category: CompanyDocumentCategory;
    contentType: string;
    fileSize?: number;
  },
): Promise<{ uploadUrl: string; key: string }> {
  const res = await apiFetch<Envelope<{ uploadUrl: string; key: string }>>(
    `/api/companies/${companyId}/documents/upload-url`,
    { method: 'POST', headers: authHeaders(), body: JSON.stringify(input) },
  );
  return res.data;
}

export async function createCompanyDocument(
  companyId: string,
  input: {
    name: string;
    category: CompanyDocumentCategory;
    s3Key: string;
    description?: string;
    fileType?: string;
    fileSize?: number;
  },
): Promise<CompanyDocument> {
  const res = await apiFetch<Envelope<{ document: CompanyDocument }>>(
    `/api/companies/${companyId}/documents`,
    { method: 'POST', headers: authHeaders(), body: JSON.stringify(input) },
  );
  return res.data.document;
}

export async function deleteCompanyDocument(companyId: string, documentId: string): Promise<void> {
  await apiFetch<Envelope<{ deleted: true; id: string }>>(
    `/api/companies/${companyId}/documents/${documentId}`,
    { method: 'DELETE', headers: authHeaders() },
  );
}
